import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  CreditCard,
  Mail,
  Save,
  TestTube,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  AlertCircle,
  Send,
  Shield,
  Zap,
  Building2,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Payment settings state
  const [paymentSettings, setPaymentSettings] = useState({
    provider: 'stripe',
    is_enabled: false,
    is_test_mode: true,
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    currency: 'USD',
  });
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    provider: 'smtp',
    is_enabled: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    sendgrid_api_key: '',
    resend_api_key: '',
    from_email: '',
    from_name: 'GRC Educators',
    reply_to_email: '',
  });
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    company_name: 'GRC EDUCATORS INC',
    tagline: 'YOUR PATH TO EXCELLENCE',
    address: '1312 17th Street, Unit #2229',
    city: 'Denver',
    state: 'Colorado',
    zip_code: '80202',
    country: 'USA',
    phone: '713-401-9995',
    email: 'support@grceducators.com',
    billing_email: 'billing@grceducators.com',
    website: 'www.grceducators.com',
    ein: '',
  });
  
  // Test email dialog
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);
  const [testEmailData, setTestEmailData] = useState({
    to_email: '',
    subject: 'Test Email from GRC Educators',
    message: 'This is a test email to verify your email settings are working correctly.',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const [paymentRes, emailRes, companyRes] = await Promise.all([
        adminAPI.getPaymentSettings(),
        adminAPI.getEmailSettings(),
        adminAPI.getCompanySettings(),
      ]);
      
      setPaymentSettings(prev => ({
        ...prev,
        ...paymentRes.data,
        stripe_secret_key: '', // Don't show actual key
        stripe_webhook_secret: '',
      }));
      
      setEmailSettings(prev => ({
        ...prev,
        ...emailRes.data,
        smtp_password: '',
        sendgrid_api_key: '',
        resend_api_key: '',
      }));
      
      if (companyRes.data) {
        setCompanySettings(prev => ({
          ...prev,
          ...companyRes.data,
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompanySettings = async () => {
    try {
      setSaving(true);
      await adminAPI.updateCompanySettings(companySettings);
      toast.success('Company settings saved successfully');
    } catch (error) {
      console.error('Error saving company settings:', error);
      toast.error('Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePaymentSettings = async () => {
    try {
      setSaving(true);
      await adminAPI.updatePaymentSettings(paymentSettings);
      toast.success('Payment settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestPaymentConnection = async () => {
    try {
      setTesting(true);
      const response = await adminAPI.testPaymentConnection();
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    try {
      setSaving(true);
      await adminAPI.updateEmailSettings(emailSettings);
      toast.success('Email settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTesting(true);
      const response = await adminAPI.testEmailSettings(testEmailData);
      toast.success(response.data.message);
      setShowTestEmailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl font-bold text-navy-700">Settings</h2>
        <p className="text-slate-500">Configure company info, payment gateway, and email server settings</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="company" className="data-[state=active]:bg-white rounded-lg px-6">
            <Building2 className="w-4 h-4 mr-2" />
            Company
          </TabsTrigger>
          <TabsTrigger value="payment" className="data-[state=active]:bg-white rounded-lg px-6">
            <CreditCard className="w-4 h-4 mr-2" />
            Payment Gateway
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-white rounded-lg px-6">
            <Mail className="w-4 h-4 mr-2" />
            Email Server
          </TabsTrigger>
        </TabsList>

        {/* Company Settings Tab */}
        <TabsContent value="company">
          <Card className="border-slate-200 rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-navy-600" />
                    Company Information
                  </CardTitle>
                  <CardDescription>This information will appear on invoices, emails, and contact page</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={companySettings.company_name}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="GRC EDUCATORS INC"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input
                    value={companySettings.tagline}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, tagline: e.target.value }))}
                    placeholder="YOUR PATH TO EXCELLENCE"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Street Address *</Label>
                <Input
                  value={companySettings.address}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="1312 17th Street, Unit #2229"
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={companySettings.city}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Denver"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Input
                    value={companySettings.state}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="Colorado"
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP Code *</Label>
                  <Input
                    value={companySettings.zip_code}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, zip_code: e.target.value }))}
                    placeholder="80202"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country *</Label>
                  <Input
                    value={companySettings.country}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, country: e.target.value }))}
                    placeholder="USA"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone Number *</Label>
                  <Input
                    value={companySettings.phone}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="713-401-9995"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Support Email *</Label>
                  <Input
                    type="email"
                    value={companySettings.email}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="support@grceducators.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Billing Email</Label>
                  <Input
                    type="email"
                    value={companySettings.billing_email}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, billing_email: e.target.value }))}
                    placeholder="billing@grceducators.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={companySettings.website}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="www.grceducators.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>EIN (Tax ID)</Label>
                <Input
                  value={companySettings.ein}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, ein: e.target.value }))}
                  placeholder="XX-XXXXXXX"
                />
                <p className="text-xs text-slate-500">Optional - appears on invoices</p>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleSaveCompanySettings}
                  disabled={saving}
                  className="bg-navy-700 hover:bg-navy-800"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Company Settings
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Settings Tab */}
        <TabsContent value="payment">
          <Card className="border-slate-200 rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-navy-600" />
                    Payment Gateway Settings
                  </CardTitle>
                  <CardDescription>Configure your Stripe payment integration</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={paymentSettings.is_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                    {paymentSettings.is_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <Badge className={paymentSettings.is_test_mode ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}>
                    {paymentSettings.is_test_mode ? 'Test Mode' : 'Live Mode'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Switch */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-gold-500" />
                  <div>
                    <p className="font-medium">Enable Payment Processing</p>
                    <p className="text-sm text-slate-500">Allow customers to make payments</p>
                  </div>
                </div>
                <Switch
                  checked={paymentSettings.is_enabled}
                  onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, is_enabled: checked }))}
                />
              </div>

              {/* Test/Live Mode Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium">Test Mode</p>
                    <p className="text-sm text-slate-500">Use test keys for development</p>
                  </div>
                </div>
                <Switch
                  checked={paymentSettings.is_test_mode}
                  onCheckedChange={(checked) => setPaymentSettings(prev => ({ ...prev, is_test_mode: checked }))}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Provider */}
                <div className="space-y-2">
                  <Label>Payment Provider</Label>
                  <Select 
                    value={paymentSettings.provider} 
                    onValueChange={(value) => setPaymentSettings(prev => ({ ...prev, provider: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stripe">Stripe</SelectItem>
                      <SelectItem value="paypal" disabled>PayPal (Coming Soon)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Currency */}
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select 
                    value={paymentSettings.currency} 
                    onValueChange={(value) => setPaymentSettings(prev => ({ ...prev, currency: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Stripe Keys */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-navy-700">Stripe API Keys</h4>
                
                <div className="space-y-2">
                  <Label>Publishable Key</Label>
                  <Input
                    value={paymentSettings.stripe_publishable_key}
                    onChange={(e) => setPaymentSettings(prev => ({ ...prev, stripe_publishable_key: e.target.value }))}
                    placeholder="pk_test_..."
                  />
                  <p className="text-xs text-slate-500">This key is safe to expose in frontend code</p>
                </div>

                <div className="space-y-2">
                  <Label>Secret Key</Label>
                  <div className="relative">
                    <Input
                      type={showSecretKey ? 'text' : 'password'}
                      value={paymentSettings.stripe_secret_key}
                      onChange={(e) => setPaymentSettings(prev => ({ ...prev, stripe_secret_key: e.target.value }))}
                      placeholder={paymentSettings.stripe_secret_key_masked || 'sk_test_...'}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecretKey(!showSecretKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Keep this key secret. Leave empty to keep existing key.</p>
                </div>

                <div className="space-y-2">
                  <Label>Webhook Secret (Optional)</Label>
                  <div className="relative">
                    <Input
                      type={showWebhookSecret ? 'text' : 'password'}
                      value={paymentSettings.stripe_webhook_secret}
                      onChange={(e) => setPaymentSettings(prev => ({ ...prev, stripe_webhook_secret: e.target.value }))}
                      placeholder={paymentSettings.stripe_webhook_secret_masked || 'whsec_...'}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">For handling Stripe webhooks</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleTestPaymentConnection}
                  disabled={testing || !paymentSettings.stripe_secret_key_masked}
                  className="rounded-xl"
                >
                  {testing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4 mr-2" />
                  )}
                  Test Connection
                </Button>
                <Button
                  onClick={handleSavePaymentSettings}
                  disabled={saving}
                  className="bg-navy-600 hover:bg-navy-700 rounded-xl"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="email">
          <Card className="border-slate-200 rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-navy-600" />
                    Email Server Settings
                  </CardTitle>
                  <CardDescription>Configure your email delivery service</CardDescription>
                </div>
                <Badge className={emailSettings.is_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                  {emailSettings.is_enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Switch */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gold-500" />
                  <div>
                    <p className="font-medium">Enable Email Service</p>
                    <p className="text-sm text-slate-500">Send transactional emails to users</p>
                  </div>
                </div>
                <Switch
                  checked={emailSettings.is_enabled}
                  onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, is_enabled: checked }))}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Provider */}
                <div className="space-y-2">
                  <Label>Email Provider</Label>
                  <Select 
                    value={emailSettings.provider} 
                    onValueChange={(value) => setEmailSettings(prev => ({ ...prev, provider: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="smtp">SMTP Server</SelectItem>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                      <SelectItem value="resend">Resend</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* From Email */}
                <div className="space-y-2">
                  <Label>From Email *</Label>
                  <Input
                    type="email"
                    value={emailSettings.from_email}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, from_email: e.target.value }))}
                    placeholder="noreply@grceducators.com"
                  />
                </div>

                {/* From Name */}
                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input
                    value={emailSettings.from_name}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, from_name: e.target.value }))}
                    placeholder="GRC Educators"
                  />
                </div>

                {/* Reply To */}
                <div className="space-y-2">
                  <Label>Reply-To Email</Label>
                  <Input
                    type="email"
                    value={emailSettings.reply_to_email}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, reply_to_email: e.target.value }))}
                    placeholder="support@grceducators.com"
                  />
                </div>
              </div>

              {/* SMTP Settings */}
              {emailSettings.provider === 'smtp' && (
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-navy-700">SMTP Configuration</h4>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Host *</Label>
                      <Input
                        value={emailSettings.smtp_host}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SMTP Port</Label>
                      <Input
                        type="number"
                        value={emailSettings.smtp_port}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                        placeholder="587"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Username *</Label>
                      <Input
                        value={emailSettings.smtp_username}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_username: e.target.value }))}
                        placeholder="your@email.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password *</Label>
                      <div className="relative">
                        <Input
                          type={showSmtpPassword ? 'text' : 'password'}
                          value={emailSettings.smtp_password}
                          onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                          placeholder={emailSettings.smtp_password_masked || 'App password'}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailSettings.smtp_use_tls}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_use_tls: e.target.checked }))}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm">Use TLS</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailSettings.smtp_use_ssl}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_use_ssl: e.target.checked }))}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-sm">Use SSL</span>
                    </label>
                  </div>
                </div>
              )}

              {/* SendGrid Settings */}
              {emailSettings.provider === 'sendgrid' && (
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-navy-700">SendGrid Configuration</h4>
                  <div className="space-y-2">
                    <Label>API Key *</Label>
                    <div className="relative">
                      <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={emailSettings.sendgrid_api_key}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, sendgrid_api_key: e.target.value }))}
                        placeholder={emailSettings.sendgrid_api_key_masked || 'SG.xxxx...'}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Resend Settings */}
              {emailSettings.provider === 'resend' && (
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-navy-700">Resend Configuration</h4>
                  <div className="space-y-2">
                    <Label>API Key *</Label>
                    <div className="relative">
                      <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={emailSettings.resend_api_key}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, resend_api_key: e.target.value }))}
                        placeholder={emailSettings.resend_api_key_masked || 're_xxxx...'}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowTestEmailDialog(true)}
                  disabled={!emailSettings.is_enabled}
                  className="rounded-xl"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Email
                </Button>
                <Button
                  onClick={handleSaveEmailSettings}
                  disabled={saving}
                  className="bg-navy-600 hover:bg-navy-700 rounded-xl"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test Email Dialog */}
      <Dialog open={showTestEmailDialog} onOpenChange={setShowTestEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-navy-600" />
              Send Test Email
            </DialogTitle>
            <DialogDescription>
              Send a test email to verify your email settings are working correctly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient Email *</Label>
              <Input
                type="email"
                value={testEmailData.to_email}
                onChange={(e) => setTestEmailData(prev => ({ ...prev, to_email: e.target.value }))}
                placeholder="your@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={testEmailData.subject}
                onChange={(e) => setTestEmailData(prev => ({ ...prev, subject: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Input
                value={testEmailData.message}
                onChange={(e) => setTestEmailData(prev => ({ ...prev, message: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowTestEmailDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleTestEmail}
              disabled={testing || !testEmailData.to_email}
              className="bg-navy-600 hover:bg-navy-700"
            >
              {testing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Test
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
